import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isInitialQueryLoading } from '../../../lib/query/listQuery';
import { usePagedListUrlState } from '../../../lib/query/usePagedListUrlState';
import { useUrlListFilter, serializeValue } from '../../../lib/query/useUrlListFilter';
import { OFFERING_FILTER_FIELDS } from '../../../routes/filterMaps';
import { Button, Typography, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../../../components/DataTable';
import { ListPage, buildCogActionsColumn } from '../../../components/list';
import OfferingFilterBar, { type OfferingFilterValues } from './OfferingFilterBar';
import OfferingViewDrawer from './OfferingViewDrawer';
import OfferingFormDrawer from './OfferingFormDrawer';
import { useOfferings } from '../hooks/useOfferings';
import { useOffering } from '../hooks/useOffering';
import { useCreateOffering } from '../hooks/useCreateOffering';
import { useUpdateOffering } from '../hooks/useUpdateOffering';
import { useDeleteOffering } from '../hooks/useDeleteOffering';
import type { OfferingListItem, OfferingFormValues } from '../types';
import Can from '../../permissions/Can';

const defaultFilter: OfferingFilterValues = {
  search: undefined,
  status: undefined,
  offeringCategoryId: undefined,
  hasActiveDiscount: undefined,
  minBasePrice: undefined,
  maxBasePrice: undefined,
};

function formatAmount(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const OfferingsPage: React.FC = () => {
  const { page, pageSize, setPage, setPageSize } = usePagedListUrlState(10);
  const [filter] = useUrlListFilter(defaultFilter, OFFERING_FILTER_FIELDS);
  const [selectedOfferingId, setSelectedOfferingId] = useState<string | null>(null);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [formOfferingId, setFormOfferingId] = useState<string | null>(null);

  const listParams = useMemo(
    () => ({
      pageNumber: page,
      pageSize,
      search: filter.search || undefined,
      status: filter.status,
      offeringCategoryId: filter.offeringCategoryId,
      hasActiveDiscount: filter.hasActiveDiscount,
      minBasePrice: filter.minBasePrice,
      maxBasePrice: filter.maxBasePrice,
    }),
    [
      page,
      pageSize,
      filter.search,
      filter.status,
      filter.offeringCategoryId,
      filter.hasActiveDiscount,
      filter.minBasePrice,
      filter.maxBasePrice,
    ]
  );

  const { data: paged, isLoading } = useOfferings(listParams);
  const loadingList = isInitialQueryLoading(isLoading, paged);
  const { data: offeringDetail, isLoading: loadingOffering } = useOffering(
    formMode === 'edit' ? formOfferingId : null
  );
  const viewOffering = useOffering(selectedOfferingId);
  const deleteMutation = useDeleteOffering();

  const createMutation = useCreateOffering(() => {
    setFormDrawerOpen(false);
  });
  const updateMutation = useUpdateOffering(formOfferingId, () => {
    setFormDrawerOpen(false);
  });

  const handleAddOffering = useCallback(() => {
    setFormMode('add');
    setFormOfferingId(null);
    setFormDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((id: string) => {
    setFormMode('edit');
    setFormOfferingId(id);
    setFormDrawerOpen(true);
  }, []);

  const handleEditFromView = useCallback(() => {
    if (selectedOfferingId) {
      setSelectedOfferingId(null);
      handleEdit(selectedOfferingId);
    }
  }, [selectedOfferingId, handleEdit]);

  const columns: ColumnsType<OfferingListItem> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
      },
      {
        title: 'Base Price',
        dataIndex: 'basePrice',
        key: 'basePrice',
        render: (v: number) => formatAmount(v),
      },
      {
        title: 'Effective Price',
        dataIndex: 'effectivePrice',
        key: 'effectivePrice',
        render: (v: number | null) => (v !== null ? formatAmount(v) : '-'),
      },
      {
        title: 'Category',
        dataIndex: 'offeringCategoryTitle',
        key: 'offeringCategoryTitle',
        render: (v: string | null) => (v ?? '-'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: number) => {
          return status === 0 ? <Tag color="green">Active</Tag> : <Tag color="default">Deactivated</Tag>;
        },
      },
      {
        title: 'Discount',
        dataIndex: 'discountSummary',
        key: 'discountSummary',
        render: (v: string | null, record: OfferingListItem) => {
          if (!v) return '-';
          const text = v.replaceAll('$', '').trim();
          return record.hasActiveDiscount
            ? <Tag color="green">{text}</Tag>
            : <Tag color="blue">{text}</Tag>;
        },
      },
      buildCogActionsColumn<OfferingListItem>({
        onView: (record) => setSelectedOfferingId(record.id),
        onEdit: (record) => handleEdit(record.id),
        onDelete: (record) => deleteMutation.mutate(record.id),
        deleteConfirm: () => ({
          title: 'Delete offering',
          content: 'Are you sure you want to delete this offering?',
        }),
        rowNumber: (_record, index) => (page - 1) * pageSize + index + 1,
      }),
    ],
    [deleteMutation, handleEdit, page, pageSize],
  );

  const handleSaveForm = (values: OfferingFormValues) => {
    if (formMode === 'add') {
      createMutation.mutate(values);
    } else if (formOfferingId) {
      updateMutation.mutate(values);
    }
  };

  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    } else {
      setPage(newPage);
    }
  }, [pageSize, setPage, setPageSize]);

  const [, rawSetSearchParams] = useSearchParams();

  const handleFilterChange = useCallback((next: OfferingFilterValues) => {
    rawSetSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('page');
        for (const def of OFFERING_FILTER_FIELDS) {
          const val = (next as Record<string, unknown>)[def.field];
          const serialized = serializeValue(val, def);
          if (serialized !== undefined) {
            nextParams.set(def.param, serialized);
          } else {
            nextParams.delete(def.param);
          }
        }
        return nextParams;
      },
      { replace: true },
    );
  }, [rawSetSearchParams]);

  const handleFilterReset = useCallback(() => {
    rawSetSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('page');
        for (const def of OFFERING_FILTER_FIELDS) {
          nextParams.delete(def.param);
        }
        return nextParams;
      },
      { replace: true },
    );
  }, [rawSetSearchParams]);

  return (
    <>
      <ListPage
        title="Offerings"
        subtitle="Manage catalog items, pricing, discounts, and categories."
        actions={(
          <Can permission="offerings.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddOffering}>
              Add Offering
            </Button>
          </Can>
        )}
        filters={(
          <OfferingFilterBar
            value={filter}
            onChange={handleFilterChange}
            onReset={handleFilterReset}
          />
        )}
      >
        <DataTable<OfferingListItem>
          columns={columns}
          dataSource={paged?.items ?? []}
          loading={loadingList}
          rowKey="id"
          bordered
          striped
          pagination={
            paged
              ? {
                  current: paged.pageNumber,
                  pageSize: paged.pageSize,
                  total: paged.totalCount,
                  onChange: handlePaginationChange,
                }
              : undefined
          }
          emptyText="No offerings yet. Add your first offering."
        />
      </ListPage>

      <OfferingViewDrawer
        open={selectedOfferingId != null}
        onClose={() => setSelectedOfferingId(null)}
        offering={viewOffering.data}
        loading={viewOffering.isLoading}
        onEdit={handleEditFromView}
      />

      <OfferingFormDrawer
        open={formDrawerOpen}
        onClose={() => setFormDrawerOpen(false)}
        mode={formMode}
        offeringId={formOfferingId}
        offering={offeringDetail}
        loadingOffering={formMode === 'edit' && formOfferingId ? loadingOffering : false}
        onSave={handleSaveForm}
        saving={createMutation.isPending || updateMutation.isPending}
      />
    </>
  );
};

export default OfferingsPage;
